import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { mapBackendToUI, mapUIToBackend } from "../utils/eventMapper";

const useCalendarEvents = (API_BASE, currentInfinityCode, eventTypes, t) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchEvents = async () => {
    if (!currentInfinityCode) {
      setErrorMsg(t.noUserSession);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const url = `${API_BASE}/${currentInfinityCode}`;
      const response = await apiFetch(url, { method: "GET" });
      const res = await response.json();

      let eventsArray = [];

      if (Array.isArray(res)) {
        eventsArray = res;
      } else if (res.data?.success && Array.isArray(res.data.data)) {
        eventsArray = res.data.data;
      } else if (res.data?.data && Array.isArray(res.data.data)) {
        eventsArray = res.data.data;
      }

      if (eventsArray.length === 0) {
        setEvents([]);
        setErrorMsg(t.noEventsFound);
      } else {
        const uiEvents = eventsArray.map((ev) => mapBackendToUI(ev, eventTypes, t));
        setEvents(uiEvents);
        return uiEvents;
      }
    } catch (err) {
      console.error("Failed to fetch events:", err);
      setErrorMsg(t.unableToLoad);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async (formData) => {
    const backendPayload = mapUIToBackend(formData, currentInfinityCode);
    await apiFetch(API_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(backendPayload),
    });
  };

  const updateEvent = async (id, formData) => {
    const backendPayload = mapUIToBackend(formData, currentInfinityCode);
    await apiFetch(`${API_BASE}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(backendPayload),
    });
  };

  const deleteEvent = async (id) => {
    await apiFetch(`${API_BASE}/${id}`, { method: "DELETE" });
    setEvents((prev) => prev.filter((ev) => ev.id !== id));
  };

  const updateRSVP = async (eventId, newStatus) => {
    const response = await apiFetch(
      `${API_BASE}/events/${eventId}/rsvp/${currentInfinityCode}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      }
    );
    return await response.json();
  };

  const getEventsForDate = (date, filterType) => {
    if (!date) return [];
    const { formatDate } = require("../utils/dateUtils");
    const dateString = formatDate(date);
    return events.filter(
      (ev) =>
        ev.date === dateString &&
        (filterType === "all" || ev.type === filterType)
    );
  };

  const getUpcomingEvents = (filterType, searchTerm) => {
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

    return events
      .filter((event) => {
        if (filterType !== "all" && event.type !== filterType) return false;
        if (
          searchTerm &&
          !event.title.toLowerCase().includes(searchTerm.toLowerCase())
        )
          return false;

        const eventDate = new Date(`${event.date}T00:00:00`);
        return eventDate >= today && eventDate <= nextWeek;
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 8);
  };

  return {
    events,
    setEvents,
    loading,
    errorMsg,
    setErrorMsg,
    fetchEvents,
    createEvent,
    updateEvent,
    deleteEvent,
    updateRSVP,
    getEventsForDate,
    getUpcomingEvents,
  };
};

export default useCalendarEvents;
